'use client'

import { useState } from 'react'
import { ZOHO_AUDIT_OFFER, ZOHO_PLAN_FOOTNOTE, ZOHO_PLANS, type ZohoPlan } from '@/lib/zoho-rescue-data'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import { AuditIcon, CardCheck, ChevronDownIcon, KeyIcon, TableCheck, TableCross } from '@/components/zoho/icons'

const VISIBLE_FEATURES = 7
const rescue = ZOHO_PLANS[0]
const stack = ZOHO_PLANS[1]

function trackPlan(plan: ZohoPlan, type: 'primary' | 'secondary') {
  trackPostHogEvent('cta_click', {
    cta_text: `Get started - ${plan.name}`,
    cta_location: 'zoho_plans',
    cta_type: type,
    plan: plan.id,
  })
}

function PlanCard({ plan }: { plan: ZohoPlan }) {
  const visible = plan.features.slice(0, VISIBLE_FEATURES)

  return (
    <article
      className={`relative flex flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_24px_80px_rgba(15,35,70,0.12)] ${
        plan.recommended
          ? 'ring-2 ring-[#13479c] ring-offset-4 ring-offset-[#eef4fc]'
          : 'border border-slate-200'
      }`}
    >
      <div className="flex flex-1 flex-col px-7 pb-7 pt-8 sm:px-8">
        <h3 className="text-[28px] font-black tracking-[-0.04em] text-[#0a2b5c] sm:text-[32px]">{plan.name}</h3>
        <p className="mt-2 text-[16px] font-semibold leading-[1.4] text-[#13479c]">{plan.tagline}</p>

        <div className="mt-6 flex items-end gap-1.5">
          <span className="text-[44px] font-black leading-none tracking-[-0.04em] text-[#0a2b5c]">
            {plan.priceDisplay}
          </span>
          <span className="mb-1.5 text-[15px] font-semibold text-slate-400">{plan.period}</span>
        </div>

        <p className="mt-5 text-[14.5px] leading-[1.55] text-slate-600">{plan.fitFor}</p>

        {plan.inheritsLabel && (
          <p className="mt-7 text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#13479c]">
            {plan.inheritsLabel}
          </p>
        )}

        <ul className={`${plan.inheritsLabel ? 'mt-3' : 'mt-7'} space-y-3.5`}>
          {visible.map((feature) => (
            <li key={feature.label} className="flex items-start gap-2.5">
              <CardCheck />
              <span className="min-w-0 text-[15px] leading-[1.45] text-slate-700">
                <span className="font-semibold text-[#0a2b5c]">{feature.label}</span>
                <span className="text-slate-500"> - {feature.detail}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-8">
          <a
            href="#get-quote"
            className={`inline-flex h-12 w-full items-center justify-center rounded-full px-7 text-[15px] font-bold transition-colors ${
              plan.recommended
                ? 'bg-[#13479c] text-white shadow-[0_10px_24px_rgba(19,71,156,0.24)] hover:bg-[#0f3a7a]'
                : 'border border-[#13479c] bg-white text-[#13479c] hover:bg-blue-50'
            }`}
            onClick={() => trackPlan(plan, plan.recommended ? 'primary' : 'secondary')}
          >
            Get started
          </a>
        </div>
      </div>
    </article>
  )
}

function FeatureRow({
  label,
  detail,
  inRescue,
}: {
  label: string
  detail: string
  inRescue: boolean
}) {
  return (
    <tr className="border-t border-slate-100">
      <th scope="row" className="min-w-0 px-3 py-3 text-left align-top sm:px-6 sm:py-4 sm:pr-8">
        <div className="text-[13.5px] font-semibold leading-[1.35] text-[#0a2b5c] sm:text-[15px]">{label}</div>
        <div className="mt-0.5 text-[12px] leading-[1.45] text-slate-500 sm:text-[13px]">{detail}</div>
      </th>
      <td className="w-14 px-1 py-3 text-center align-middle sm:w-auto sm:px-2 sm:py-4">
        {inRescue ? <TableCheck /> : <TableCross />}
        <span className="sr-only">{inRescue ? 'Included' : 'Not included'}</span>
      </td>
      <td className="w-14 px-1 py-3 text-center align-middle sm:w-auto sm:px-2 sm:py-4">
        <TableCheck />
        <span className="sr-only">Included</span>
      </td>
    </tr>
  )
}

function CompareTable() {
  return (
    <div className="max-w-full overflow-x-auto rounded-[20px] bg-white shadow-[0_24px_80px_rgba(15,35,70,0.12)] sm:rounded-[28px]">
      <table className="w-full table-fixed border-collapse sm:table-auto">
        <thead>
          <tr className="border-b border-slate-200">
            <th
              className="w-auto bg-slate-50 sm:w-[42%] sm:min-w-[240px] sm:bg-cover sm:bg-center sm:bg-no-repeat sm:bg-[url('/images/services/zoho-services.webp')]"
            >
              <span className="sr-only">Zoho services</span>
            </th>
            <th className="w-16 bg-slate-50/70 px-1 py-4 text-center sm:w-[29%] sm:min-w-[200px] sm:px-4 sm:py-6">
              <div className="text-[12px] font-black leading-tight tracking-[-0.03em] text-[#0a2b5c] sm:text-[20px]">{rescue.name}</div>
              <div className="mt-1 text-[13px] font-black text-[#0a2b5c] sm:mt-2 sm:text-[22px]">
                {rescue.priceDisplay}
                <span className="ml-0.5 text-[11px] font-semibold text-slate-400 sm:ml-1 sm:text-[13px]">{rescue.period}</span>
              </div>
            </th>
            <th className="w-16 px-1 py-4 text-center sm:w-[29%] sm:min-w-[200px] sm:px-4 sm:py-6">
              <div className="text-[12px] font-black leading-tight tracking-[-0.03em] text-[#0a2b5c] sm:text-[20px]">
                <span className="sm:hidden">Stack</span>
                <span className="hidden sm:inline">{stack.name}</span>
              </div>
              <div className="mt-1 text-[13px] font-black text-[#0a2b5c] sm:mt-2 sm:text-[22px]">
                {stack.priceDisplay}
                <span className="ml-0.5 text-[11px] font-semibold text-slate-400 sm:ml-1 sm:text-[13px]">{stack.period}</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th
              colSpan={3}
              className="bg-slate-50 px-3 py-2.5 text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400 sm:px-6 sm:py-3 sm:text-[12px]"
            >
              Rescue
            </th>
          </tr>
          {rescue.features.map((feature) => (
            <FeatureRow key={feature.label} {...feature} inRescue />
          ))}
          <tr>
            <th
              colSpan={3}
              className="bg-slate-50 px-3 py-2.5 text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400 sm:px-6 sm:py-3 sm:text-[12px]"
            >
              Rescue + Stack
            </th>
          </tr>
          {stack.features.map((feature) => (
            <FeatureRow key={feature.label} {...feature} inRescue={false} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function ZohoPlans() {
  const [compareOpen, setCompareOpen] = useState(false)
  const [auditCheckoutLoading, setAuditCheckoutLoading] = useState(false)
  const [auditCheckoutError, setAuditCheckoutError] = useState<string | null>(null)

  async function startAuditCheckout() {
    if (auditCheckoutLoading) return
    setAuditCheckoutError(null)
    setAuditCheckoutLoading(true)

    trackPostHogEvent('cta_click', {
      cta_text: ZOHO_AUDIT_OFFER.cta,
      cta_location: 'zoho_plans',
      cta_type: 'secondary',
      plan: ZOHO_AUDIT_OFFER.id,
    })

    try {
      const res = await fetch('/api/services/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product: ZOHO_AUDIT_OFFER.productId }),
      })
      const data = await res.json()

      if (!res.ok || !data.url) {
        setAuditCheckoutError(data.error || 'Could not start checkout. Please try again.')
        setAuditCheckoutLoading(false)
        return
      }

      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'ph_checkout_started',
          serviceName: ZOHO_AUDIT_OFFER.name,
          serviceSlug: ZOHO_AUDIT_OFFER.slug,
          totalPrice: ZOHO_AUDIT_OFFER.price,
        })
      }

      window.location.href = data.url
    } catch {
      setAuditCheckoutError('Network error. Please try again.')
      setAuditCheckoutLoading(false)
    }
  }

  return (
    <section id="plans" className="scroll-mt-24 bg-[#eef4fc] px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">Pricing</p>
          <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
            Two engagements. Pick the one that sounds like your situation.
          </h2>
        </div>

        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
          {ZOHO_PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>

        <article className="mt-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,35,70,0.08)]">
          <div className="flex flex-col gap-6 px-7 py-7 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
            <div className="flex max-w-2xl items-center gap-4 sm:gap-5">
              <AuditIcon />
              <div>
                <h3 className="text-[22px] font-black tracking-[-0.04em] text-[#0a2b5c] sm:text-[26px]">
                  {ZOHO_AUDIT_OFFER.headline}
                </h3>
                <p className="mt-2 text-[15px] leading-[1.55] text-slate-600">
                  {ZOHO_AUDIT_OFFER.description}
                  <br />
                  {ZOHO_AUDIT_OFFER.credit}
                </p>
              </div>
            </div>
            <div className="flex w-full shrink-0 flex-col items-center gap-4 sm:flex-row sm:justify-center lg:w-auto lg:flex-col lg:items-end lg:justify-start">
              <span className="text-[44px] font-black leading-none tracking-[-0.04em] text-[#0a2b5c]">
                {ZOHO_AUDIT_OFFER.priceDisplay}
              </span>
              <button
                type="button"
                disabled={auditCheckoutLoading}
                className="inline-flex h-12 items-center justify-center rounded-full border border-[#13479c] bg-white px-7 text-[15px] font-bold text-[#13479c] transition-colors hover:bg-blue-50 disabled:opacity-60"
                onClick={startAuditCheckout}
              >
                {auditCheckoutLoading ? 'Redirecting…' : ZOHO_AUDIT_OFFER.cta}
              </button>
              {auditCheckoutError && (
                <p className="max-w-[16rem] text-center text-[13px] text-red-600 lg:text-right">
                  {auditCheckoutError}
                </p>
              )}
            </div>
          </div>
          <p className="flex items-start gap-2 border-t border-slate-100 px-7 py-4 text-[13px] leading-[1.55] text-slate-500 sm:px-8 sm:text-[13.5px]">
            <KeyIcon />
            <span>{ZOHO_AUDIT_OFFER.disclaimer}</span>
          </p>
        </article>

        <p className="mx-auto mt-10 max-w-2xl text-center text-[14.5px] leading-[1.6] text-slate-500">
          {ZOHO_PLAN_FOOTNOTE}
        </p>

        <div className="mt-10 text-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-[15px] font-bold text-[#13479c] underline-offset-4 hover:underline"
            aria-expanded={compareOpen}
            onClick={() => setCompareOpen((open) => !open)}
          >
            {compareOpen ? 'Hide full comparison' : 'Compare everything'}
            <ChevronDownIcon className={`transition-transform ${compareOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {compareOpen && <div className="mt-8"><CompareTable /></div>}
      </div>
    </section>
  )
}
